// Pure CID re-derivation shared across the write, verify, and serve paths (szzt-spec.md §7.2, §9.2).
//
// Every CID a consumer checks is recomputed here from raw record/blob bytes using the SAME primitives
// as the build (`@szzt/core` `dagCbor`/`rawCid`) — never trusted from a source's assertion. Two
// distinct CIDs (§7.2), do not conflate:
//   (a) root/manifest CID = dCBOR42 CID of the inline MASL      → recomputeRootCid(masl)
//   (b) manifest-record CID = atproto CID of the whole record   → recomputeRecordCid(value)

import { dagCbor, rawCid } from "@szzt/core";
import type { MaslDocument } from "@szzt/core";
import { ResolveError } from "./types.ts";

/** Parsed pieces of an `at://did/collection/rkey` URI. */
export interface AtUriParts {
  did: string;
  collection: string;
  rkey: string;
}

/** Parse an `at://<authority>/<collection>/<rkey>` URI. Throws {@link ResolveError} if malformed. */
export function parseAtUri(uri: string): AtUriParts {
  const m = /^at:\/\/([^/]+)\/([^/]+)\/([^/]+)$/.exec(uri);
  if (!m) throw new ResolveError(`not a record AT-URI (need at://authority/collection/rkey): ${uri}`);
  return { did: m[1]!, collection: m[2]!, rkey: m[3]! };
}

/**
 * (a) The root/manifest CID: the dCBOR42 CID of the inline MASL document. Equals the deployment's
 * `root` and the manifest record's `cid` (§7.2). Uses the same DRISL encoder as the build, so a
 * conforming build and any verifier agree byte-for-byte.
 */
export async function recomputeRootCid(masl: MaslDocument): Promise<string> {
  const { cid } = await dagCbor(masl);
  return cid;
}

/**
 * (b) The manifest-record CID (or any record's atproto CID): the dCBOR42/DRISL CID of the whole
 * lex-encoded record value. The `value` MUST be in IPLD-JSON shape — cid-link fields as `{ $link }`,
 * strong-ref `cid` fields as plain strings, optional fields omitted — matching what the PDS encodes.
 */
export async function recomputeRecordCid(value: unknown): Promise<string> {
  const { cid } = await dagCbor(value);
  return cid;
}

/** The `raw` CID of exact blob bytes (§3.2, §7.2). What every resource `src.ref.$link` must equal. */
export async function recomputeBlobCid(bytes: Uint8Array): Promise<string> {
  return rawCid(bytes);
}
